import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useCart } from '@/contexts/CartContext'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { gtmEvents } from '@/lib/gtm'
import { supabase } from '@/lib/supabase/client'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

// Schema definition using Zod
const checkoutSchema = z.object({
  name: z.string().min(2, 'Nome completo é obrigatório'),
  email: z.string().email('E-mail inválido'),
  phone: z.string().min(10, 'Telefone inválido'),
  zip: z.string().min(8, 'CEP inválido'),
  address: z.string().min(3, 'Endereço obrigatório'),
  number: z.string().min(1, 'Número obrigatório'),
  neighborhood: z.string().min(2, 'Bairro obrigatório'),
  complement: z.string().optional(),
  city: z.string().min(2, 'Cidade obrigatória'),
  state: z.string().length(2, 'Use a sigla do estado (ex: SP)'),
  payment: z.enum(['credit_card', 'pix', 'boleto'], {
    required_error: 'Selecione um método de pagamento',
  }),
})

type CheckoutFormValues = z.infer<typeof checkoutSchema>

const Checkout = () => {
  const { items, cartTotal, clearCart } = useCart()
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const shipping = items.length > 0 ? 15.0 : 0
  const total = cartTotal + shipping

  // Safe fetch for site settings to ensure connection works and no runtime errors occur
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { error } = await supabase
          .from('site_settings' as any)
          .select('*')
          .single()

        if (error) {
          console.error('Error fetching settings:', error)
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err)
      }
    }

    fetchSettings()
  }, [])

  // Initialize form
  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      zip: '',
      address: '',
      number: '',
      neighborhood: '',
      complement: '',
      city: '',
      state: '',
      payment: 'credit_card',
    },
    mode: 'onChange',
  })

  // Prefill data if user is logged in
  useEffect(() => {
    if (user && !form.formState.isDirty) {
      form.reset({
        name: user.user_metadata?.full_name || user.user_metadata?.name || '',
        email: user.email || '',
        phone: user.user_metadata?.phone || '',
        zip: '',
        address: '',
        number: '',
        neighborhood: '',
        complement: '',
        city: '',
        state: '',
        payment: 'credit_card',
      })
    }
  }, [user, form])

  const getGtmItems = () =>
    items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
      item_category: item.category,
      item_variant: item.size ? `${item.size}/${item.color}` : undefined,
    }))

  const nextStep = async () => {
    let fieldsToValidate: (keyof CheckoutFormValues)[] = []

    if (step === 1) {
      fieldsToValidate = ['name', 'email', 'phone']
    } else if (step === 2) {
      fieldsToValidate = [
        'zip',
        'address',
        'number',
        'neighborhood',
        'city',
        'state',
      ]
    }

    const isValid = await form.trigger(fieldsToValidate)

    if (isValid) {
      if (step === 2) {
        gtmEvents.addShippingInfo(getGtmItems(), total, 'standard')
      }
      setStep((prev) => prev + 1)
    }
  }

  const prevStep = () => {
    setStep((prev) => Math.max(1, prev - 1))
  }

  const onSubmit = async (data: CheckoutFormValues) => {
    setIsSubmitting(true)

    // 1. Track Payment Info
    gtmEvents.addPaymentInfo(getGtmItems(), total, data.payment)

    const transactionId = `TRX-${Math.floor(Math.random() * 1000000)}`

    try {
      // 2. Prepare Order Payload for RPC
      const orderData = {
        transaction_id: transactionId,
        user_id: user?.id ?? null,
        customer_name: data.name,
        customer_email: data.email,
        customer_phone: data.phone,
        address: data.address,
        address_number: data.number,
        neighborhood: data.neighborhood,
        complement: data.complement || '',
        city: data.city,
        state: data.state,
        zip: data.zip,
        payment_method: data.payment,
        shipping_cost: shipping,
        total_amount: total,
        status: 'paid',
      }

      const itemsData = items.map((item) => ({
        product_id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        variant: item.size ? `${item.size}/${item.color}` : null,
      }))

      // 3. Save Order using RPC to ensure atomic transaction and bypass RLS for guests
      const { data: order, error: orderError } = await supabase.rpc(
        'create_order',
        {
          order_data: orderData,
          items_data: itemsData,
        },
      )

      if (orderError) {
        console.error('Order Creation Error:', orderError)
        throw new Error('Falha ao criar o pedido. Tente novamente.')
      }

      // 4. Prepare data for Thank You page (session storage)
      const sessionOrderData = {
        transactionId,
        items,
        total,
        shipping,
        customer: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          number: data.number,
          neighborhood: data.neighborhood,
          complement: data.complement,
          city: data.city,
          state: data.state,
          zip: data.zip,
          country: 'BR',
        },
      }

      sessionStorage.setItem('lastOrder', JSON.stringify(sessionOrderData))
      toast.success('Pedido realizado com sucesso!')
      clearCart() // Clear cart after successful order

      // 5. Navigate to Success Page
      navigate('/thank-you')
    } catch (error: any) {
      console.error('Error placing order:', error)
      toast.error(error.message || 'Erro ao processar pedido. Tente novamente.')
      setIsSubmitting(false)
    }
  }

  // Loading state for auth check
  if (authLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (items.length === 0 && !isSubmitting) {
    // Short timeout to allow render before redirecting
    setTimeout(() => navigate('/carrinho'), 0)
    return null
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Checkout</h1>
        <div className="hidden md:flex gap-2 text-sm font-medium">
          <span
            className={step >= 1 ? 'text-primary' : 'text-muted-foreground'}
          >
            1. Identificação
          </span>
          <span className="text-muted-foreground">{'>'}</span>
          <span
            className={step >= 2 ? 'text-primary' : 'text-muted-foreground'}
          >
            2. Entrega
          </span>
          <span className="text-muted-foreground">{'>'}</span>
          <span
            className={step >= 3 ? 'text-primary' : 'text-muted-foreground'}
          >
            3. Pagamento
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Main Form Area */}
        <div className="md:col-span-2 space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {step === 1 && (
                <div className="bg-white p-6 rounded-lg border shadow-sm space-y-4 animate-in fade-in slide-in-from-left-4">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Seus Dados</h2>
                    {!user && (
                      <Button
                        variant="link"
                        asChild
                        className="p-0 h-auto text-primary"
                      >
                        <Link to="/login?redirectTo=/checkout">
                          Já tem conta? Faça login
                        </Link>
                      </Button>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Completo</FormLabel>
                        <FormControl>
                          <Input placeholder="João Silva" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-mail</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="joao@email.com"
                            type="email"
                            disabled={!!user}
                            className={user ? 'bg-slate-50' : ''}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefone</FormLabel>
                        <FormControl>
                          <Input placeholder="(11) 99999-9999" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="button"
                    className="w-full mt-4"
                    onClick={nextStep}
                  >
                    Continuar para Entrega
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white p-6 rounded-lg border shadow-sm space-y-4 animate-in fade-in slide-in-from-left-4">
                  <h2 className="text-xl font-semibold mb-4">
                    Endereço de Entrega
                  </h2>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="zip"
                      render={({ field }) => (
                        <FormItem className="col-span-2 sm:col-span-1">
                          <FormLabel>CEP</FormLabel>
                          <FormControl>
                            <Input placeholder="00000-000" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem className="col-span-4 sm:col-span-3">
                          <FormLabel>Endereço</FormLabel>
                          <FormControl>
                            <Input placeholder="Rua das Flores" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="number"
                      render={({ field }) => (
                        <FormItem className="col-span-4 sm:col-span-1">
                          <FormLabel>Número</FormLabel>
                          <FormControl>
                            <Input placeholder="123" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="neighborhood"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bairro</FormLabel>
                          <FormControl>
                            <Input placeholder="Centro" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="complement"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Complemento</FormLabel>
                          <FormControl>
                            <Input placeholder="Apto 101" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cidade</FormLabel>
                          <FormControl>
                            <Input placeholder="São Paulo" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Estado (UF)</FormLabel>
                          <FormControl>
                            <Input placeholder="SP" maxLength={2} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex gap-4 mt-4">
                    <Button type="button" variant="outline" onClick={prevStep}>
                      Voltar
                    </Button>
                    <Button type="button" className="flex-1" onClick={nextStep}>
                      Continuar para Pagamento
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="bg-white p-6 rounded-lg border shadow-sm space-y-4 animate-in fade-in slide-in-from-left-4">
                  <h2 className="text-xl font-semibold mb-4">Pagamento</h2>

                  <FormField
                    control={form.control}
                    name="payment"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-col space-y-1"
                          >
                            <FormItem className="flex items-center space-x-3 space-y-0 border p-4 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                              <FormControl>
                                <RadioGroupItem value="credit_card" />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer flex-1">
                                Cartão de Crédito
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0 border p-4 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                              <FormControl>
                                <RadioGroupItem value="pix" />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer flex-1">
                                PIX
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0 border p-4 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                              <FormControl>
                                <RadioGroupItem value="boleto" />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer flex-1">
                                Boleto Bancário
                              </FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-4 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      disabled={isSubmitting}
                    >
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        'Confirmar Compra'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </Form>
        </div>

        {/* Order Summary Sidebar */}
        <div className="bg-slate-50 p-6 rounded-lg border h-fit sticky top-24">
          <h3 className="font-semibold mb-4">Resumo do Pedido</h3>
          <div className="space-y-3 text-sm border-b pb-4 mb-4 max-h-64 overflow-y-auto">
            {items.map((item) => (
              <div
                key={`${item.id}-${item.size}`}
                className="flex justify-between"
              >
                <span>
                  {item.quantity}x {item.name}
                  {item.size && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({item.size})
                    </span>
                  )}
                </span>
                <span className="font-medium">
                  R$ {(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>R$ {cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Frete</span>
              <span>R$ {shipping.toFixed(2)}</span>
            </div>
          </div>
          <div className="border-t pt-4 flex justify-between font-bold text-lg">
            <span>Total</span>
            <span>R$ {total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
